import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth } from "../../context/AuthProvider";
import { updateOrderStatus } from "../../services/order.service";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
} from "firebase/firestore";
import { db } from "../../lib/firebase";
import { toast } from "react-hot-toast";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import {
  Phone,
  Navigation,
  Package,
  CheckCircle,
  Hand,
  ArrowRight,
  EyeOff,
  MapPin,
  Store,
} from "lucide-react";
import { getKitchenDetails } from "../../services/kitchen.service";
import {
  acceptDeliveryRun,
  completeDeliveryRun,
  markKitchenStopPicked,
  synthesizeRunFromOrder,
  confirmDeliveryWithOTP,
} from "../../services/delivery.service";
import { DeliveryRun, DeliveryKitchenStop } from "../../types/delivery";
import { COLLECTIONS } from "../../constants/collections";

type KitchenExtra = {
  name?: string;
  phone?: string;
  address?: string;
};

const getDropAddress = (runOrOrder: any): string =>
  runOrOrder.deliveryAddress ||
  runOrOrder.shippingAddress ||
  runOrOrder.address ||
  "Address unavailable";

const isCashToCollect = (rawPayment: string): boolean => {
  const m = String(rawPayment || "")
    .toLowerCase()
    .trim();
  return (
    m === "food" ||
    m === "cod" ||
    m === "cash" ||
    m.includes("food") ||
    m.includes("cod") ||
    m.includes("cash")
  );
};

const openMap = (address: string) => {
  if (!address || address === "Address unavailable") {
    return toast.error("Address unavailable");
  }
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`,
    "_blank"
  );
};

const tapBtnClass =
  "inline-flex items-center justify-center min-w-[44px] min-h-[44px] p-3 border rounded-full bg-white touch-manipulation";

const DeliveryDashboard: React.FC = () => {
  const { user } = useAuth();

  const [openRuns, setOpenRuns] = useState<DeliveryRun[]>([]);
  const [orphanReadyOrders, setOrphanReadyOrders] = useState<any[]>([]);
  const [myRuns, setMyRuns] = useState<DeliveryRun[]>([]);
  const [legacyMyOrders, setLegacyMyOrders] = useState<any[]>([]);
  const [cashInHand, setCashInHand] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"open" | "mine">("open");

  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [completing, setCompleting] = useState(false);

  const [activeComplete, setActiveComplete] = useState<{
    kind: "run" | "legacy";
    run?: DeliveryRun;
    order?: any;
    otp: string;
    otpRequired: boolean;
    amount: number;
    paymentMethod: "cash" | "online";
  } | null>(null);

  const [kitchenExtras, setKitchenExtras] = useState<Record<string, KitchenExtra>>(
    {}
  );
  const kitchenFetchStarted = useRef<Set<string>>(new Set());

  const ensureKitchenFallback = useCallback(
    (kitchenIds: (string | undefined)[]) => {
      for (const kid of kitchenIds) {
        if (!kid || kitchenFetchStarted.current.has(kid)) continue;
        kitchenFetchStarted.current.add(kid);
        getKitchenDetails(kid)
          .then((k) => {
            if (!k) return;
            setKitchenExtras((prev) => ({
              ...prev,
              [kid]: {
                name: k.kitchen_name || (k as any).name,
                phone: k.phone,
                address: k.address,
              },
            }));
          })
          .catch(() => {
            /* non-blocking */
          });
      }
    },
    []
  );

  const resolveStop = (stop: DeliveryKitchenStop) => {
    const extra = stop.kitchenId ? kitchenExtras[stop.kitchenId] : undefined;
    return {
      name: stop.kitchenName || extra?.name || "Kitchen",
      address: stop.kitchenAddress || extra?.address || "Address unavailable",
      phone: stop.kitchenPhone || extra?.phone || "",
    };
  };

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      if (snap.exists()) setCashInHand(snap.data().cashInHand || 0);
    });
    return () => unsub();
  }, [user]);

  // Open delivery runs (no orderBy so index is optional)
  useEffect(() => {
    const q = query(
      collection(db, COLLECTIONS.DELIVERY_RUNS),
      where("status", "==", "open")
    );
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const runs = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() } as DeliveryRun))
          .sort((a: any, b: any) => {
            const ta = a.createdAt?.toMillis?.() || a.createdAt?.seconds || 0;
            const tb = b.createdAt?.toMillis?.() || b.createdAt?.seconds || 0;
            return tb - ta;
          });
        setOpenRuns(runs);
        const kids = runs.flatMap((r) =>
          (r.kitchenStops || []).map((s) => s.kitchenId)
        );
        ensureKitchenFallback(kids);
      },
      (err) => console.warn("open delivery_runs listener:", err)
    );
    return () => unsub();
  }, [ensureKitchenFallback]);

  // Legacy / backfill: ready kitchen orders not yet in a run (solo / pre-deploy)
  useEffect(() => {
    const q = query(
      collection(db, COLLECTIONS.ORDERS),
      where("status", "==", "ready"),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const valid = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter(
          (o: any) =>
            !o.deliveryAgentId &&
            !o.selfDelivery &&
            o.status === "ready"
        );
      setOrphanReadyOrders(valid);
      ensureKitchenFallback(valid.map((o: any) => o.kitchenId));
    });
    return () => unsub();
  }, [ensureKitchenFallback]);

  // My assigned runs
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, COLLECTIONS.DELIVERY_RUNS),
      where("deliveryAgentId", "==", user.uid),
      where("status", "in", ["assigned", "out_for_delivery"])
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const runs = snapshot.docs.map(
        (d) => ({ id: d.id, ...d.data() } as DeliveryRun)
      );
      setMyRuns(runs);
      ensureKitchenFallback(
        runs.flatMap((r) => (r.kitchenStops || []).map((s) => s.kitchenId))
      );
    });
    return () => unsub();
  }, [user, ensureKitchenFallback]);

  // Backward compatible: out_for_delivery kitchen orders with no run doc
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, COLLECTIONS.ORDERS),
      where("deliveryAgentId", "==", user.uid),
      where("status", "in", ["out_for_delivery"]),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(q, (snapshot) => {
      const orders = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setLegacyMyOrders(orders);
      ensureKitchenFallback(orders.map((o: any) => o.kitchenId));
    });
    return () => unsub();
  }, [user, ensureKitchenFallback]);

  /** Merge Firestore open runs + synthetic 1-stop for orphans not covered by a run */
  const displayOpenRuns: DeliveryRun[] = useMemo(() => {
    const coveredOrderIds = new Set<string>();
    for (const run of openRuns) {
      for (const s of run.kitchenStops || []) {
        coveredOrderIds.add(s.kitchenOrderId);
      }
      for (const id of run.kitchenOrderIds || []) {
        coveredOrderIds.add(id);
      }
    }

    // Group orphans by parentOrderId — only synthesize when single kitchen or no parent
    const byParent = new Map<string, any[]>();
    const solos: any[] = [];
    for (const o of orphanReadyOrders) {
      if (coveredOrderIds.has(o.id)) continue;
      if (o.parentOrderId) {
        const key = String(o.parentOrderId);
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key)!.push(o);
      } else {
        solos.push(o);
      }
    }

    const synthetic: DeliveryRun[] = [];

    // Solo / no parent → always 1-stop synthetic
    for (const o of solos) {
      synthetic.push(synthesizeRunFromOrder(o));
    }

    // Parent groups: only synthesize complete sets (never partial multi-kitchen).
    // Prefer Cloud Function delivery_runs; client synthesizes only as CF lag / single-kitchen backfill.
    for (const [parentId, orders] of Array.from(byParent.entries())) {
      if (openRuns.some((r) => r.id === parentId || r.parentOrderId === parentId)) {
        continue;
      }
      const kc = Number(orders[0].kitchenCount) || 0;
      // Expected sibling count: kitchenCount on order, else length of kitchenOrderIds if present
      const expected =
        kc ||
        (Array.isArray(orders[0].siblingKitchenOrderIds)
          ? orders[0].siblingKitchenOrderIds.length
          : 0);

      if (orders.length === 1 && expected === 1) {
        synthetic.push(synthesizeRunFromOrder(orders[0]));
        continue;
      }

      // All siblings appear ready in the ready-pool (same parent, N ready docs)
      // and expected count matches — CF may lag; synthesize full run.
      if (expected > 1 && orders.length === expected) {
        const stops: DeliveryKitchenStop[] = orders.map((o: any) => ({
          kitchenOrderId: o.id,
          kitchenId: o.kitchenId || "",
          kitchenName: o.kitchenName || "Kitchen",
          kitchenAddress: o.kitchenAddress || "",
          kitchenPhone: o.kitchenPhone || "",
          status: "ready" as const,
          totalAmount: Number(o.totalAmount) || 0,
          paymentMethod: String(o.paymentMethod || o.paymentMode || ""),
        }));
        let cash = 0;
        for (const s of stops) {
          if (isCashToCollect(s.paymentMethod || "")) {
            cash += Number(s.totalAmount) || 0;
          }
        }
        synthetic.push({
          id: parentId,
          parentOrderId: parentId,
          customerName: orders[0].customerName || "",
          customerPhone: orders[0].customerPhone || "",
          deliveryAddress: getDropAddress(orders[0]),
          kitchenStops: stops,
          kitchenCount: stops.length,
          kitchenOrderIds: stops.map((s) => s.kitchenOrderId),
          status: "open",
          deliveryAgentId: null,
          deliveryOtp: String(
            orders[0].deliveryOtp ?? orders[0].otp ?? ""
          ).trim(),
          totalCashToCollect: cash,
          _synthetic: true,
        });
      }
      // else: partial ready → agents do not see the run yet (admin can still inspect orders)
    }

    return [...openRuns, ...synthetic];
  }, [openRuns, orphanReadyOrders]);

  /** My tasks = runs + legacy OFD orders not already in a my-run */
  const displayMyTasks = useMemo(() => {
    const covered = new Set<string>();
    for (const run of myRuns) {
      for (const s of run.kitchenStops || []) covered.add(s.kitchenOrderId);
      for (const id of run.kitchenOrderIds || []) covered.add(id);
    }
    const legacyAsRuns: DeliveryRun[] = [];
    for (const o of legacyMyOrders) {
      if (covered.has(o.id)) continue;
      if (o.selfDelivery) continue;
      // If order has deliveryRunId pointing at an active my run, skip
      if (o.deliveryRunId && myRuns.some((r) => r.id === o.deliveryRunId)) {
        continue;
      }
      legacyAsRuns.push({
        ...synthesizeRunFromOrder(o),
        status: "out_for_delivery",
        deliveryAgentId: user?.uid || null,
        _synthetic: true,
      });
    }
    return [...myRuns, ...legacyAsRuns];
  }, [myRuns, legacyMyOrders, user?.uid]);

  const handleAcceptRun = async (run: DeliveryRun) => {
    if (!user?.uid) return;
    const n = run.kitchenStops?.length || 1;
    const label =
      n > 1
        ? `Accept this run? ${n} kitchen pickups → 1 drop`
        : "Accept this delivery task?";
    if (!window.confirm(label)) return;

    setLoading(true);
    try {
      await acceptDeliveryRun(run, user.uid);
      toast.success(n > 1 ? `Run accepted · ${n} pickups 🚴` : "Order Accepted! 🚴");
      setActiveTab("mine");
    } catch (err) {
      console.error(err);
      toast.error("Failed to accept run.");
    } finally {
      setLoading(false);
    }
  };

  const onCompleteRunClick = (run: DeliveryRun) => {
    const otp = String(run.deliveryOtp ?? "").trim();
    const cash = (run.totalCashToCollect || 0) > 0;
    // Also detect cash from stops if total missing
    let amount = Number(run.totalCashToCollect) || 0;
    if (!amount) {
      for (const s of run.kitchenStops || []) {
        if (isCashToCollect(s.paymentMethod || "")) {
          amount += Number(s.totalAmount) || 0;
        }
      }
    }
    setActiveComplete({
      kind: "run",
      run,
      otp,
      otpRequired: true,
      amount,
      paymentMethod: cash || amount > 0 ? "cash" : "online",
    });
    setOtpInput("");
    setIsOtpModalOpen(true);
  };

  const handleMarkPicked = async (run: DeliveryRun, stop: DeliveryKitchenStop) => {
    if (run._synthetic) {
      toast("Pickup noted (will sync after accept)", { icon: "📦" });
      return;
    }
    try {
      await markKitchenStopPicked(run.id, stop.kitchenOrderId);
      toast.success(`Picked up: ${stop.kitchenName || "Kitchen"}`);
    } catch {
      toast.error("Could not mark picked");
    }
  };

  const handleCompleteDelivery = async () => {
    if (!activeComplete || !user?.uid) return;

    if (activeComplete.otpRequired && otpInput !== activeComplete.otp) {
      if (activeComplete.otp === "0000") {
        if (!window.confirm("Debug: System OTP is 0000. Force Complete?")) return;
      } else {
        toast.error("Incorrect Code");
        return;
      }
    }

    if (activeComplete.paymentMethod === "cash" && activeComplete.amount > 0) {
      const ok = window.confirm(
        `💰 COLLECT ₹${activeComplete.amount} CASH\n\nConfirm you received the cash?`
      );
      if (!ok) return;
    }

    setCompleting(true);
    try {
      if (activeComplete.kind === "run" && activeComplete.run) {
        await completeDeliveryRun({
          run: activeComplete.run,
          agentUid: user.uid,
          otp: otpInput || activeComplete.otp || "0000",
        });
      } else if (activeComplete.order) {
        await updateOrderStatus(activeComplete.order.id, "delivered", {
          deliveryAgentId: user.uid,
        });
        if (activeComplete.order.parentOrderId) {
          try {
            await confirmDeliveryWithOTP({
              kitchenOrderId: activeComplete.order.id,
              parentOrderId: activeComplete.order.parentOrderId,
              otp: otpInput || activeComplete.otp || "0000",
            });
          } catch (settleErr) {
            console.warn("settleDeliveredOrder optional call failed:", settleErr);
          }
        }
      }

      toast.success("Delivery Completed! 🎉");
      setIsOtpModalOpen(false);
      setActiveComplete(null);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || "Failed to update status");
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="p-4 max-w-lg mx-auto pb-24">
      <div className="flex justify-between items-center mb-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-lg font-bold text-gray-800">
            Hi, {user?.displayName?.split(" ")[0] || "Agent"}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="w-2 h-2 bg-green-500 rounded-full" />
            <span className="text-xs text-green-700 font-semibold">Active session</span>
          </div>
        </div>
        <div className="text-right bg-green-50 px-3 py-2 rounded-lg border border-green-100">
          <div className="text-[10px] text-green-600 uppercase font-bold">Cash In Hand</div>
          <div className="text-lg font-black text-green-800">₹{cashInHand}</div>
        </div>
      </div>

      <div className="flex bg-gray-100 p-1 rounded-xl mb-6">
        <button
          onClick={() => setActiveTab("open")}
          className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all ${
            activeTab === "open" ? "bg-white shadow text-blue-600" : "text-gray-500"
          }`}
        >
          Open ({displayOpenRuns.length})
        </button>
        <button
          onClick={() => setActiveTab("mine")}
          className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-all ${
            activeTab === "mine" ? "bg-white shadow text-green-600" : "text-gray-500"
          }`}
        >
          My Tasks ({displayMyTasks.length})
        </button>
      </div>

      {activeTab === "open" && (
        <div className="space-y-4">
          {displayOpenRuns.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-2 opacity-20" />
              <p>No delivery runs ready.</p>
              <p className="text-xs mt-1">Multi-kitchen runs appear when all kitchens are ready.</p>
            </div>
          ) : (
            displayOpenRuns.map((run) => {
              const drop = getDropAddress(run);
              const n = run.kitchenStops?.length || 0;
              const cash = Number(run.totalCashToCollect) || 0;
              return (
                <Card key={run.id} className="p-0 border-l-4 border-l-blue-500">
                  <div className="p-5">
                    <div className="flex justify-between mb-2 items-center">
                      <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-bold uppercase">
                        {n > 1 ? `${n} Kitchen Run` : "Ready"}
                      </span>
                      {cash > 0 ? (
                        <span className="font-bold text-orange-700">Cash ₹{cash}</span>
                      ) : (
                        <span className="text-xs font-semibold text-green-700">Paid</span>
                      )}
                    </div>

                    <div className="flex gap-2 items-start text-sm text-gray-700 bg-gray-50 p-2 rounded mb-3">
                      <MapPin size={14} className="mt-0.5 shrink-0 text-blue-500" />
                      <div className="min-w-0">
                        <div className="font-bold text-gray-800">
                          {run.customerName || "Customer"}
                        </div>
                        <div className="text-xs text-gray-500 break-words whitespace-normal">
                          {drop}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="text-[10px] uppercase font-bold text-gray-400">
                        Pickups
                      </div>
                      {(run.kitchenStops || []).map((stop) => {
                        const k = resolveStop(stop);
                        return (
                          <div
                            key={stop.kitchenOrderId}
                            className="flex items-start gap-2 text-xs border border-gray-100 rounded-lg p-2"
                          >
                            <Store size={14} className="mt-0.5 text-gray-400 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="font-bold text-gray-800">{k.name}</div>
                              <div className="text-gray-500 break-words">{k.address}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      onClick={() => handleAcceptRun(run)}
                      isLoading={loading}
                      className="w-full bg-blue-600 hover:bg-blue-700 min-h-[48px]"
                    >
                      <Hand className="mr-2" size={16} />{" "}
                      {n > 1 ? "Accept Run" : "Accept Pickup"}
                    </Button>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}

      {activeTab === "mine" && (
        <div className="space-y-4">
          {displayMyTasks.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-20" />
              <p>You have no active deliveries.</p>
            </div>
          ) : (
            displayMyTasks.map((run) => {
              const drop = getDropAddress(run);
              const cash = Number(run.totalCashToCollect) || 0;
              const n = run.kitchenStops?.length || 0;
              return (
                <Card key={run.id} className="p-0 border-l-4 border-l-green-500 shadow-lg">
                  <div className="bg-green-50 px-5 py-2 flex justify-between items-center border-b border-green-100">
                    <span className="text-xs font-bold text-green-700 uppercase flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />{" "}
                      {n > 1 ? `Live · ${n} pickups` : "Live"}
                    </span>
                    <span className="text-xs font-bold text-gray-500">
                      #{String(run.id).slice(-4).toUpperCase()}
                    </span>
                  </div>

                  <div className="p-5">
                    <div className="space-y-3 mb-4">
                      <div className="text-[10px] uppercase font-bold text-gray-400">
                        Kitchen checklist
                      </div>
                      {(run.kitchenStops || []).map((stop) => {
                        const k = resolveStop(stop);
                        const picked = stop.status === "picked";
                        return (
                          <div
                            key={stop.kitchenOrderId}
                            className="border border-gray-100 rounded-xl p-3"
                          >
                            <div className="flex justify-between items-start gap-2">
                              <div className="text-xs min-w-0 flex-1">
                                <span className="font-bold text-gray-800">{k.name}</span>
                                <p className="text-gray-500 break-words whitespace-normal">
                                  {k.address}
                                </p>
                                {picked && (
                                  <span className="inline-block mt-1 text-[10px] font-bold uppercase text-green-700 bg-green-50 px-2 py-0.5 rounded">
                                    Picked
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-2 shrink-0">
                                <button
                                  type="button"
                                  onClick={() => openMap(k.address)}
                                  className={`${tapBtnClass} text-blue-600`}
                                  aria-label="Open kitchen map"
                                >
                                  <Navigation size={18} />
                                </button>
                                {k.phone ? (
                                  <a
                                    href={`tel:${k.phone}`}
                                    className={`${tapBtnClass} text-green-600`}
                                    aria-label="Call kitchen"
                                  >
                                    <Phone size={18} />
                                  </a>
                                ) : null}
                              </div>
                            </div>
                            {!picked && !run._synthetic && (
                              <button
                                type="button"
                                onClick={() => handleMarkPicked(run, stop)}
                                className="mt-2 w-full text-xs font-bold py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 min-h-[40px]"
                              >
                                Mark picked up
                              </button>
                            )}
                          </div>
                        );
                      })}

                      <div className="border-t border-dashed border-gray-200 pt-3">
                        <div className="flex justify-between items-start gap-3">
                          <div className="text-xs min-w-0 flex-1">
                            <span className="font-bold text-gray-400 uppercase block mb-0.5">
                              Drop
                            </span>
                            <span className="font-bold text-gray-800">
                              {run.customerName || "Customer"}
                            </span>
                            <p className="text-gray-500 break-words whitespace-normal">
                              {drop}
                            </p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <button
                              type="button"
                              onClick={() => openMap(drop)}
                              className={`${tapBtnClass} text-blue-600`}
                              aria-label="Open drop-off map"
                            >
                              <Navigation size={18} />
                            </button>
                            {run.customerPhone ? (
                              <a
                                href={`tel:${run.customerPhone}`}
                                className={`${tapBtnClass} text-green-600`}
                                aria-label="Call customer"
                              >
                                <Phone size={18} />
                              </a>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>

                    {cash > 0 && (
                      <div className="mb-4 bg-orange-50 border border-orange-200 text-orange-800 p-3 rounded-xl text-center">
                        <div className="text-[10px] uppercase font-bold text-orange-600">
                          Cash to Collect (total)
                        </div>
                        <div className="text-xl font-black">₹{cash}</div>
                      </div>
                    )}

                    <Button
                      onClick={() => onCompleteRunClick(run)}
                      className="w-full py-4 text-base bg-green-600 hover:bg-green-700 shadow-green-200 shadow-lg min-h-[48px]"
                    >
                      Complete Delivery <ArrowRight className="ml-2" size={18} />
                    </Button>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}

      {isOtpModalOpen && activeComplete && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-center mb-1">Verify Delivery</h3>

            {activeComplete.otpRequired ? (
              <>
                <p className="text-center text-gray-500 text-sm mb-6">
                  Enter Customer 3-Digit Code
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={3}
                  autoFocus
                  placeholder="0 0 0"
                  value={otpInput}
                  onChange={(e) =>
                    setOtpInput(e.target.value.replace(/\D/g, "").slice(0, 3))
                  }
                  className="text-center text-4xl font-mono w-full border-b-2 border-gray-300 focus:border-green-500 outline-none py-2 mb-6"
                />
              </>
            ) : (
              <div className="text-center text-orange-600 bg-orange-50 p-3 rounded-lg mb-6 text-sm font-bold border border-orange-100">
                <EyeOff className="inline mr-1" size={16} /> Verification Skipped by Chef
              </div>
            )}

            {activeComplete.paymentMethod === "cash" && activeComplete.amount > 0 ? (
              <div className="bg-orange-50 border border-orange-200 text-orange-800 p-4 rounded-xl text-center mb-6">
                <div className="text-xs uppercase font-bold text-orange-600 mb-1">
                  Cash to Collect
                </div>
                <div className="text-3xl font-black">₹{activeComplete.amount}</div>
              </div>
            ) : (
              <div className="bg-green-50 text-green-700 p-3 rounded-lg text-center text-sm font-bold mb-6 border border-green-100">
                <CheckCircle className="inline mr-1" size={16} /> Paid Online
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="secondary"
                onClick={() => setIsOtpModalOpen(false)}
                disabled={completing}
              >
                Back
              </Button>
              <Button
                onClick={handleCompleteDelivery}
                isLoading={completing}
                disabled={activeComplete.otpRequired && otpInput.length < 3}
              >
                Complete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryDashboard;
